'use client';

import React, { useEffect, useState } from 'react';
import { userService, User } from '@/services/userService';
import { aclService } from '@/lib/api/aclService';
import { UserTable } from './UserTable';
import { UserFormModal } from './UserFormModal';
import { UserDetailModal } from './UserDetailModal';
import { Button } from '@/components/ui/Button';
import { Toast } from '@/components/ui/Toast';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Icon } from '@/components/ui/icons';
import { checkPasswordPolicy, getPasswordPolicyErrorMessage } from '@/utils/passwordPolicy';

type ToastType = "success" | "error" | "warning" | "info";

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAcl, setSelectedAcl] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  
  // Paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  // Modales
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState<Partial<User>>({});
  const [selectedUserForDetail, setSelectedUserForDetail] = useState<User | null>(null);
  
  // Confirmación de eliminación
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  // Toast
  const [toast, setToast] = useState<{ message: string; type: ToastType; isOpen: boolean }>({
    message: "",
    type: "info",
    isOpen: false,
  });
  
  // Lista de ACLs disponibles
  const [availableAcls, setAvailableAcls] = useState<string[]>([]);

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await userService.getUsers();
      if (response.data) {
        setUsers(response.data);
        setFilteredUsers(response.data);
      } else {
        setError(response.error || 'Error al cargar usuarios');
        showToast(response.error || 'Error al cargar usuarios', 'error');
      }
    } catch (err) {
      const errorMessage = 'Error de conexión al obtener usuarios';
      setError(errorMessage);
      showToast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchAcls = async () => {
    try {
      const response = await aclService.getACLs();
      if (response.data) {
        const aclNames = response.data.map(acl => acl.acl_name);
        setAvailableAcls(aclNames);
      }
    } catch (err) {
      console.error('Error al cargar ACLs:', err);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchAcls();
  }, []);

  useEffect(() => {
    let filtered = [...users];

    // Filtro de búsqueda
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(user =>
        user.name.toLowerCase().includes(term) ||
        user.userName.toLowerCase().includes(term) ||
        user.correo.toLowerCase().includes(term) ||
        user.acl.toLowerCase().includes(term) ||
        (user.puesto && user.puesto.toLowerCase().includes(term)) ||
        (user.region && user.region.toLowerCase().includes(term))
      );
    }

    // Filtro por ACL
    if (selectedAcl !== 'all') {
      filtered = filtered.filter(user => user.acl === selectedAcl);
    }

    // Filtro por estado
    if (selectedStatus !== 'all') {
      filtered = filtered.filter(user => user.userEstatus === selectedStatus);
    }

    setFilteredUsers(filtered);
    setCurrentPage(1); // Reset a la primera página cuando cambian los filtros
  }, [searchTerm, selectedAcl, selectedStatus, users]);

  // Calcular paginación
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedUsers = filteredUsers.slice(startIndex, endIndex);

  const showToast = (message: string, type: ToastType) => {
    setToast({ message, type, isOpen: true });
    setTimeout(() => setToast({ ...toast, isOpen: false }), 3000);
  };

  const handleEdit = (user: User) => {
    setCurrentUser(user);
    setIsModalOpen(true);
  };

  const handleCreate = () => {
    setCurrentUser({});
    setIsModalOpen(true);
  };

  const handleViewDetail = (user: User) => {
    setSelectedUserForDetail(user);
    setIsDetailModalOpen(true);
  };

  const handleDelete = (user: User) => {
    setUserToDelete(user);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!userToDelete) return;

    try {
      const response = await userService.deleteUser(userToDelete.ownerID);
      if (response.data) {
        showToast(response.data.message || 'Usuario eliminado exitosamente', 'success');
        await fetchUsers();
      } else {
        showToast(response.error || 'Error al eliminar el usuario', 'error');
      }
    } catch (err) {
      showToast('Error de conexión al eliminar el usuario', 'error');
    } finally {
      setShowDeleteConfirm(false);
      setUserToDelete(null);
    }
  };

  const handleToggleStatus = async (user: User) => {
    const newStatus = user.userEstatus === 'Activo' ? 'Inactivo' : 'Activo';
    try {
      const response = await userService.updateUser(user.ownerID, {
        userEstatus: newStatus,
      });
      if (response.data) {
        showToast(`Usuario ${newStatus === 'Activo' ? 'activado' : 'desactivado'} exitosamente`, 'success');
        await fetchUsers();
      } else {
        showToast(response.error || 'Error al actualizar el estado del usuario', 'error');
      }
    } catch (err) {
      showToast('Error de conexión al actualizar el estado', 'error');
    }
  };

  const handleSubmit = async (userData: Partial<User>) => {
    let response;
    if (userData.ownerID) {
      // Actualizar usuario existente
      response = await userService.updateUser(userData.ownerID, userData);
      if (response.data) {
        showToast('Usuario actualizado exitosamente', 'success');
        setIsModalOpen(false);
        await fetchUsers();
      } else {
        throw new Error(response.error || 'Error al actualizar el usuario');
      }
    } else {
      // Crear nuevo usuario
      // Validar campos requeridos
      if (!userData.userName || !userData.name || !userData.correo || !userData.password) {
        throw new Error('Por favor completa todos los campos requeridos');
      }

      const check = checkPasswordPolicy(userData.password || '');
      if (!check.isValid) {
        throw new Error(getPasswordPolicyErrorMessage(check) || 'La contraseña no cumple los requisitos');
      }

      // Validar ACL específicamente
      if (!userData.acl || userData.acl.trim() === '') {
        throw new Error('Por favor selecciona un rol (ACL) válido');
      }

      response = await userService.createUser(userData);
      if (response.data) {
        showToast('Usuario creado exitosamente', 'success');
        setIsModalOpen(false);
        await fetchUsers();
      } else {
        // Lanzar error para que el modal lo capture y muestre
        throw new Error(response.error || 'Error al crear el usuario');
      }
    }
  };

  if (loading && users.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#0196A1]"></div>
        <span className="ml-3 text-gray-600">Cargando usuarios...</span>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-6">
        {/* Header Section */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-6 rounded-xl shadow-sm border border-gray-100">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Gestión de Usuarios</h1>
            <p className="text-sm text-gray-500 mt-1">Administra los accesos y perfiles de la plataforma</p>
          </div>
          <Button 
            onClick={handleCreate} 
            className="bg-[#0196A1] hover:bg-[#017a85] text-white"
          >
            <span className="mr-2">+</span>
            Nuevo Usuario
          </Button>
        </div>

        {/* Filters Section */}
        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 space-y-4">
          {/* Búsqueda */}
          <div className="relative max-w-md">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Icon name="search" size={20} color="#9CA3AF" />
            </div>
            <input
              type="text"
              className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#0196A1] focus:border-[#0196A1] sm:text-sm transition-all"
              placeholder="Buscar por nombre, correo, usuario, rol, puesto o región..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Filtros adicionales */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Filtrar por Rol</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0196A1] focus:border-[#0196A1] bg-white shadow-sm"
                value={selectedAcl}
                onChange={(e) => setSelectedAcl(e.target.value)}
              >
                <option value="all">Todos los roles</option>
                {availableAcls.map(acl => (
                  <option key={acl} value={acl}>{acl}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Filtrar por Estado</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0196A1] focus:border-[#0196A1] bg-white shadow-sm"
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
              >
                <option value="all">Todos los estados</option>
                <option value="Activo">Activo</option>
                <option value="Inactivo">Inactivo</option>
                <option value="Baja">Baja</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Elementos por página</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0196A1] focus:border-[#0196A1] bg-white shadow-sm"
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
            </div>
          </div>
        </div>

        {/* Content Section */}
        {error && !loading ? (
          <div className="p-4 bg-red-50 text-red-700 rounded-lg border border-red-200">
            <p className="font-bold">Error al cargar datos</p>
            <p>{error}</p>
            <Button 
              onClick={fetchUsers} 
              className="mt-2 bg-red-600 hover:bg-red-700 text-white"
            >
              Reintentar
            </Button>
          </div>
        ) : (
          <>
            <UserTable 
              users={paginatedUsers} 
              onEdit={handleEdit}
              onDelete={handleDelete}
              onViewDetail={handleViewDetail}
              onToggleStatus={handleToggleStatus}
            />

            {/* Paginación */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                <div className="text-sm text-gray-700">
                  Mostrando {startIndex + 1} a {Math.min(endIndex, filteredUsers.length)} de {filteredUsers.length} usuarios
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                  >
                    Anterior
                  </Button>
                  <span className="text-sm text-gray-700">
                    Página {currentPage} de {totalPages}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Siguiente
                  </Button>
                </div>
              </div>
            )}

            {filteredUsers.length === 0 && !loading && (
              <div className="text-center py-12 bg-white rounded-xl shadow-sm border border-gray-100">
                <Icon name="user" size={48} className="mx-auto text-gray-400 mb-4" />
                <p className="text-gray-500 text-lg">No se encontraron usuarios</p>
                <p className="text-gray-400 text-sm mt-1">
                  {searchTerm || selectedAcl !== 'all' || selectedStatus !== 'all' 
                    ? 'Intenta ajustar los filtros de búsqueda' 
                    : 'Crea tu primer usuario para comenzar'}
                </p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modales */}
      <UserFormModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleSubmit}
        initialUser={currentUser}
        availableAcls={availableAcls}
      />

      <UserDetailModal
        isOpen={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        user={selectedUserForDetail}
      />

      {/* Confirmación de eliminación */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setUserToDelete(null);
        }}
        onConfirm={confirmDelete}
        title="Eliminar Usuario"
        message={`¿Estás seguro de que deseas eliminar al usuario "${userToDelete?.name}"? Esta acción no se puede deshacer.`}
        confirmText="Eliminar"
        cancelText="Cancelar"
        variant="danger"
      />

      {/* Toast */}
      <Toast
        message={toast.message}
        type={toast.type}
        isOpen={toast.isOpen}
        onClose={() => setToast({ ...toast, isOpen: false })}
      />
    </>
  );
};

export default UserManagement;
